import { StatusBadge as DsStatusBadge } from "@xplatform/design-system";
import type { StatusTone } from "@xplatform/design-system";
import { Loader } from "@mantine/core";
import { IconCheck, IconX, IconAlertTriangle } from "@tabler/icons-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  status: string;
  size?: "sm" | "md";
}

interface StatusPresentation {
  tone: StatusTone;
  labelKey: string;
  icon: ReactNode;
}

/**
 * Maps a translation-service status onto the design-system tone. The service
 * reports `partly_completed`, which the design system's status table does not
 * know, so the tone is passed explicitly rather than inferred.
 */
const presentationFor = (status: string): StatusPresentation => {
  switch (status.toLowerCase()) {
    case "completed":
      return {
        tone: "success",
        labelKey: "translation.status.completed",
        icon: <IconCheck size={12} />,
      };
    case "partly_completed":
      return {
        tone: "warning",
        labelKey: "translation.status.partly",
        icon: <IconAlertTriangle size={12} />,
      };
    case "failed":
      return {
        tone: "error",
        labelKey: "translation.status.failed",
        icon: <IconX size={12} />,
      };
    default:
      return {
        tone: "processing",
        labelKey: "translation.status.pending",
        icon: <Loader size={10} color="currentColor" />,
      };
  }
};

/** Translated, icon-led wrapper around the design-system `StatusBadge`. */
export const StatusBadge: React.FC<Props> = ({ status, size = "sm" }) => {
  const { t } = useTranslation();
  const { tone, labelKey, icon } = presentationFor(status);
  return (
    <DsStatusBadge tone={tone} size={size} variant="light" leftSection={icon}>
      {t(labelKey)}
    </DsStatusBadge>
  );
};
